import { useEffect, useState, useCallback } from "react";
import { MapPin, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AttendanceSettingsCard } from "@/components/attendance/AttendanceSettingsCard";
import { HolidaysCard } from "@/components/HolidaysCard";

const emptyOffice = { name: "", address: "", latitude: "", longitude: "", radius: 100 };

export default function Geofence() {
  const [offices, setOffices] = useState([]);
  const [form, setForm] = useState(emptyOffice);

  const load = useCallback(() => {
    api.get("/offices").then((r) => setOffices(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("GPS tidak didukung");
    navigator.geolocation.getCurrentPosition((pos) => {
      setForm((f) => ({ ...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) }));
      toast.success("Koordinat lokasi Anda dimuat");
    }, () => toast.error("Gagal mendapatkan lokasi"));
  };

  const save = async () => {
    if (!form.name || !form.latitude || !form.longitude) return toast.error("Nama, latitude, dan longitude wajib diisi");
    try {
      const body = { ...form, latitude: parseFloat(form.latitude), longitude: parseFloat(form.longitude), radius: parseInt(form.radius) || 100 };
      if (form.id) await api.put(`/offices/${form.id}`, body);
      else await api.post("/offices", body);
      toast.success("Lokasi kantor tersimpan");
      setForm(emptyOffice); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => { try { await api.delete(`/offices/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Lokasi & Geofence</h1>
        <p className="mt-1 text-sm text-slate-500">Konfigurasi titik kantor, radius absensi GPS, dan aturan jam kerja.</p>
      </div>

      <AttendanceSettingsCard />
      <HolidaysCard />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="rounded-2xl border-slate-200 p-6 dark:border-slate-800 lg:col-span-1">
          <h3 className="font-heading text-lg font-semibold">{form.id ? "Edit Lokasi" : "Tambah Lokasi Kantor"}</h3>
          <div className="mt-4 space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Nama Kantor</Label>
              <Input data-testid="office-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Alamat</Label>
              <Input data-testid="office-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label className="text-xs">Latitude</Label>
                <Input data-testid="office-lat-input" className="font-mono" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Longitude</Label>
                <Input data-testid="office-lng-input" className="font-mono" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Radius (meter)</Label>
              <Input data-testid="geofence-radius-input" type="number" className="font-mono" value={form.radius} onChange={(e) => setForm({ ...form, radius: e.target.value })} /></div>
            <Button variant="outline" className="w-full gap-2" onClick={useMyLocation} data-testid="use-my-location-button">
              <MapPin className="h-4 w-4" /> Gunakan Lokasi Saya
            </Button>
            <Button className="w-full gap-2" onClick={save} data-testid="save-office-button">
              {form.id ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {form.id ? "Simpan Perubahan" : "Tambah Lokasi"}
            </Button>
            {form.id && <Button variant="ghost" className="w-full" onClick={() => setForm(emptyOffice)}>Batal Edit</Button>}
          </div>
        </Card>

        <div className="space-y-4 lg:col-span-2">
          {offices.length === 0 && (
            <Card className="flex flex-col items-center gap-2 rounded-2xl border-dashed p-10 text-center text-slate-400">
              <MapPin className="h-8 w-8" /><p>Belum ada lokasi kantor terdaftar.</p>
            </Card>
          )}
          {offices.map((o) => (
            <Card key={o.id} data-testid="office-card" className="rounded-2xl border-slate-200 p-5 dark:border-slate-800">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary"><MapPin className="h-5 w-5" /></div>
                  <div>
                    <p className="font-semibold">{o.name}</p>
                    <p className="text-sm text-slate-500">{o.address}</p>
                    <p className="mt-1 font-mono text-xs text-slate-400">{o.latitude}, {o.longitude} · radius {o.radius}m</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" data-testid="edit-office-button" onClick={() => setForm({ ...o, latitude: String(o.latitude), longitude: String(o.longitude) })}>Edit</Button>
                  <Button size="icon" variant="ghost" onClick={() => del(o.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
